import React, { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { FormGroup, Label, Input, FormFeedback } from "reactstrap";

interface TextFieldProps {
  label?: string;
  type: any;
  name: string;
  value: string;
  setValue: (name: string, value: string) => void;
  setError?: (name: string, error: boolean) => void;
  placeholder?: string;
  validation?: (value: string) => boolean;
  errorMessage?: string;
  error?: boolean;
  required?: boolean;
  rows?: number; 
  disabled?: boolean;
  design?: "flex" | "default"; 
  passCol?: string;
  enableAddressFinder?: boolean; 
  maxChars?: number;             
  tabIndex? : number;            
  style? :any;
  showClearIcon?: boolean;
  autoComplete?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const TextField: FC<TextFieldProps> = ({
  label,
  type = "text",
  name,
  value,
  setValue,
  setError,
  placeholder = "",
  validation = () => true,
  errorMessage = "Invalid input",
  error = false,
  required = false,
  rows = 3, 
  disabled,
  design = "default", 
  passCol,
  enableAddressFinder = false, 
  maxChars,
  tabIndex,
  style,
  showClearIcon = false,
  autoComplete = 'on',
  onBlur
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (enableAddressFinder && inputRef.current) {
      const script = document.createElement("script");
      script.src = "https://api.addressfinder.io/assets/v3/widget.js";
      script.async = true;

      script.onload = () => {
        const widget = new (window as any).AddressFinder.Widget(
          inputRef.current,
          "Y4KUGQHWNVB7ARMCF6DP",
          "AU", 
          {}
        );
        if (typeof widget.on === "function") {
          widget.on("address:select", (fullAddress: string, metaData: any) => {
            setValue(name, fullAddress);
          });
        } else {
          console.error("The widget does not have an 'on' method.");
        }
      };
      
      document.body.appendChild(script); 
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [enableAddressFinder, name, setValue]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value: inputValue } = e.target;
    const charCount = inputValue.replace(/\s/g, "").length;
    if (maxChars && charCount > maxChars) {
      return; 
    }
    setValue(name, inputValue);
    if (setError) {
      setError(name, false);
    }
  };

  const handleClear = () => {
    setValue(name, "");
    if (setError) {
      setError(name, false);
    }
  };

  const handleBlur = () => {
    if (!validation(value) && setError) {
      setError(name, true);
    }
  };

  const flexClass = design === "flex" && windowWidth > 768 ? "d-flex align-items-center" : "";
  const labelWidthClass = design === "flex" && windowWidth > 768 ? "col-3" : "";
  const passColClass = windowWidth > 768 ? passCol : "";

  return (
    <FormGroup className={flexClass}>
      {label && (
        <Label for={name} className={`${design === "flex" ? "mr-5 " : ""}${labelWidthClass}`}>
          {label}
          {required && <span className="text-danger ml-2">*</span>}
        </Label>
      )}
      <div className={`${passColClass} position-relative`}>
        {error && <FormFeedback className="d-block mt-1">{errorMessage}</FormFeedback>}
        <Input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={handleInputChange}
          tabIndex={tabIndex} 
          onBlur={e => { handleBlur(); onBlur?.(e); }}
          placeholder={placeholder}
          disabled={disabled}
          style={style}
          invalid={error}
          innerRef={enableAddressFinder ? inputRef : undefined} 
          rows={type === "textarea" ? rows : undefined}
          autoComplete={autoComplete}
        />
        {showClearIcon && value && (
          <i 
            className="icon-close position-absolute" 
            style={{ 
              right: '10px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              cursor: 'pointer',
              color: '#666'
            }}
            onClick={handleClear}
          />
        )}
      </div>
    </FormGroup>
  );
};

export default TextField;
